import type { DragEndEvent, DragOverEvent, DragStartEvent } from "@dnd-kit/core"
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCorners } from "@dnd-kit/core"
import { useState, useMemo } from "react"
import { router } from "@inertiajs/react"
import { KanbanColumn } from "./kanban-column"
import { KanbanCard } from "./kanban-card"
import type { Project, PipeStatusRecord, ProjectStatus } from "@/types"

type Props = {
  projects: Project[]
  status: ProjectStatus
  pipeStatuses: PipeStatusRecord[]
}

export function KanbanBoard({ projects: initialProjects, status, pipeStatuses }: Props) {
  const columns = useMemo(
    () => pipeStatuses.filter((ps) => ps.parent_status === status).sort((a, b) => a.position - b.position),
    [pipeStatuses, status],
  )
  const columnIds = useMemo(() => columns.map((c) => c.id), [columns])

  const [projects, setProjects] = useState(initialProjects)
  const [activeProject, setActiveProject] = useState<Project | null>(null)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  const projectsByStatus = useMemo(() => {
    const map: Record<string, Project[]> = {}
    for (const col of columns) {
      map[col.id] = []
    }
    for (const project of projects) {
      if (map[project.pipe_status_id]) {
        map[project.pipe_status_id].push(project)
      }
    }
    return map
  }, [projects, columns])

  const handleDragStart = (event: DragStartEvent) => {
    const project = projects.find((p) => p.id === event.active.id)
    setActiveProject(project || null)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over) return

    const activeId = active.id as string
    const overId = over.id as string

    const activeProject = projects.find((p) => p.id === activeId)
    if (!activeProject) return

    const targetStatusId = columnIds.includes(overId)
      ? overId
      : projects.find((p) => p.id === overId)?.pipe_status_id

    if (targetStatusId && targetStatusId !== activeProject.pipe_status_id) {
      setProjects((prev) => prev.map((p) => (p.id === activeId ? { ...p, pipe_status_id: targetStatusId } : p)))
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active } = event
    const project = projects.find((p) => p.id === active.id)
    const originalProject = initialProjects.find((p) => p.id === active.id)

    if (project && originalProject && project.pipe_status_id !== originalProject.pipe_status_id) {
      router.patch(
        `/projects/${project.id}/update_pipe_status`,
        {
          pipe_status_id: project.pipe_status_id,
        },
        { preserveScroll: true },
      )
    }

    setActiveProject(null)
  }

  const handleDragCancel = () => {
    setProjects(initialProjects)
    setActiveProject(null)
  }

  if (columns.length === 0) {
    return <p className="text-muted-foreground text-center text-sm">Aucun suivi disponible pour ce statut</p>
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map((col) => (
          <KanbanColumn
            key={col.id}
            id={col.id}
            title={col.label}
            color={col.bg_color}
            projects={projectsByStatus[col.id] || []}
          />
        ))}
      </div>

      <DragOverlay>{activeProject && <KanbanCard project={activeProject} />}</DragOverlay>
    </DndContext>
  )
}
